import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { useSelector } from "src/store";
import { setPaneTab, showDetails, updateSearchQuery } from "src/ui/containers/dashboard/devices/store";
import { selectDevicesByGroupIds } from "src/ui/containers/dashboard/devices/store/selectors/devices";
import { MinimalTableCell } from "src/ui/shared/table/MinimalTableComponents";

import { Device, DeviceState } from "@dashboard/devices/types";
import { selectGroups } from "@dashboard/groups/store/selectors";
import ElasticService from "@services/elasticService";
import UserSettingsService from "@services/userSettingsService";
import QueryBuilder from "@shared/form/QueryBuilderField/QueryBuilderField.utils";
import { TextLink } from "@shared/TextLink";

import { TransformedApp } from "./ManagedAppsCard";

const hasCorrectPackage = (app: TransformedApp, packages: DeviceState["packages"]) => {
    return packages.some((item) => item.package === app.package && item.versionCode === app.versionCode);
};

const getDeviceWithCorrectApp = (app: TransformedApp, devices: (Device | null)[]) => {
    return devices.filter((item) => (item?.lastState ? hasCorrectPackage(app, item.lastState.packages) : false))[0];
};

interface TableItemProps {
    count: number;
    version: number;
    apps: TransformedApp;
    filteredGroupIds: number[];
}

const TableItem = (props: TableItemProps) => {
    const { t } = useTranslation(["common"]);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const groups = useSelector(selectGroups);
    const filteredDevices = useSelector((state) => selectDevicesByGroupIds(state, props.filteredGroupIds));
    const filteredGroups = groups.filter((group) => props.filteredGroupIds.includes(group.id));

    const handleLicensesOnClick = () => {
        const equalHash = QueryBuilder.getEqualHash();
        const filters = QueryBuilder.parseFiltersToString([
            {
                key: "device.lastState.packages.versionCode",
                operator: equalHash,
                value: String(props.apps.versionCode),
            },
            { key: "device.lastState.packages.package", operator: equalHash, value: props.apps.package },
        ]);
        dispatch(updateSearchQuery(filters));
        navigate(`/dashboard/devices?search=${encodeURIComponent(filters)}`);
    };

    const handleLicenseOnClick = (app: TransformedApp) => {
        const device = getDeviceWithCorrectApp(app, filteredDevices);
        if (!device) {
            return;
        }

        const group = filteredGroups.find((g) => g.licenseIds.includes(device.licenseId));
        if (group) {
            UserSettingsService.setWorkspaceValue("devicePage.columnFilters", []);

            dispatch(setPaneTab("APPS"));
            dispatch(showDetails({ type: "license", selectedId: device.licenseId }));
            handleLicensesOnClick();
            ElasticService.createTransaction("overviewPageLinkClick", {
                tab: "managed-applications",
                text: props.count,
            });
        }
    };

    if (props.count === 1) {
        return (
            <MinimalTableCell align="right">
                <TextLink onClick={() => handleLicenseOnClick(props.apps)} tooltip={t("common:tooltip.showDevices", { count: 1 })}>
                    {props.count}
                </TextLink>
            </MinimalTableCell>
        );
    }

    return (
        <MinimalTableCell align="right">
            <TextLink onClick={handleLicensesOnClick} tooltip={t("common:tooltip.showDevices", { count: props.count })}>
                {props.count}
            </TextLink>
        </MinimalTableCell>
    );
};

export default TableItem;
